import { type PermissionInputs, resolvePermissions } from './resolve-permissions.ts';
import { type DatabasePermissions } from './types.ts';

type PermissionsListener = (permissions: DatabasePermissions) => void;

/**
 * Single source of truth for database permissions.
 *
 * `isViewingVersion` and `isEditEnabled` are immutable after construction —
 * they're determined by the URL and plugin state at init. The manager is
 * rebuilt on navigation, so they never need runtime updates.
 *
 * Only `operations` (from token refresh) and `isLocked` (from Y.js) change
 * at runtime. Wire to CollabManager.onPermissionsChanged and
 * YDatabase.observeSettings — do NOT use Redux subscribe.
 */
export class DatabasePermissionsManager {
  readonly #inputs: PermissionInputs;
  readonly #listeners = new Set<PermissionsListener>();

  #permissions: DatabasePermissions;
  #disposed = false;

  constructor(initialInputs: PermissionInputs) {
    this.#inputs = { ...initialInputs };
    this.#permissions = resolvePermissions(this.#inputs);
  }

  getPermissions(): DatabasePermissions {
    return this.#permissions;
  }

  setOperations(ops: {
    canUpdateMetadata: boolean;
    canUpdateEntries: boolean;
    canDelete: boolean;
  }): void {
    this.#inputs.canUpdateMetadata = ops.canUpdateMetadata;
    this.#inputs.canUpdateEntries = ops.canUpdateEntries;
    this.#inputs.canDelete = ops.canDelete;

    this.#reconcile();
  }

  setLocked(isLocked: boolean): void {
    this.#inputs.isLocked = isLocked;

    this.#reconcile();
  }

  subscribe(listener: PermissionsListener): () => void {
    this.#listeners.add(listener);

    return () => {
      this.#listeners.delete(listener);
    };
  }

  dispose(): void {
    this.#disposed = true;
    this.#listeners.clear();
  }

  #reconcile(): void {
    if (this.#disposed) {
      return;
    }

    const prev = this.#permissions;
    const next = resolvePermissions(this.#inputs);

    if (
      prev.canEditContent === next.canEditContent &&
      prev.canEditFields === next.canEditFields &&
      prev.canDelete === next.canDelete &&
      prev.isLocked === next.isLocked &&
      prev.isViewingVersion === next.isViewingVersion
    ) {
      return;
    }

    this.#permissions = next;

    for (const listener of this.#listeners) {
      listener(next);
    }
  }
}
